import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { apiPostJson } from '../../../../support/aiApi';
import TuningAdminTable, { safePreview } from './TuningAdminTable';

import GenerationRunDetailDialog from './runs/GenerationRunDetailDialog';
import ValidateConfirmDialog from './runs/ValidateConfirmDialog';

import {
  countWords,
  getRagChunks,
  statusChip,
  VersionScopeStack,
} from './runs/runDiagnosticsUtils';

import {
  currentGenerationRunIdOf,
  generationRunHasValidation,
  getCurrentGenerationRun,
  rootGenerationRunIdOf,
} from './runs/generationRunValidationState';

type ValidationDialogMode = 'validate' | 'revalidate';

type GenerationDetailFocus =
  | 'overview'
  | 'db-context'
  | 'rag'
  | 'llm-request'
  | 'llm-response'
  | 'validation'
  | 'questions'
  | 'raw';

function extractGenerationRunFromResponse(data: any, fallback: any) {
  if (!data) return fallback;

  return (
    data.best_generation_run ||
    data.final_generation_run ||
    data.generation_run_record ||
    data.generation_run ||
    data.run ||
    data.final_run ||
    data.updated_generation_run ||
    data.regenerated_generation_run ||
    data.latest_generation_run ||
    fallback
  );
}

function extractValidationRunFromResponse(data: any) {
  if (!data) return null;

  return (
    data.validation_run ||
    data.final_validation_run ||
    data.latest_validation_run ||
    data.best_validation_run ||
    data.validation ||
    null
  );
}

function stableRunKeyOf(row: any): string {
  const current = getCurrentGenerationRun(row);

  return String(
    rootGenerationRunIdOf(row) ||
    currentGenerationRunIdOf(row) ||
    current?.id ||
    row?.id ||
    '',
  );
}

const GenerationRunsPanel: React.FC = () => {
  const queryClient = useQueryClient();

  const [selectedGenerationRun, setSelectedGenerationRun] = React.useState<any | null>(null);
  const [generationDetailOpen, setGenerationDetailOpen] = React.useState(false);
  const [generationDetailFocus, setGenerationDetailFocus] =
    React.useState<GenerationDetailFocus>('overview');

  const [validateError, setValidateError] = React.useState<string | null>(null);
  const [pendingValidationRun, setPendingValidationRun] = React.useState<any | null>(null);
  const [validationDialogMode, setValidationDialogMode] =
    React.useState<ValidationDialogMode>('validate');

  const [activeValidationRunKey, setActiveValidationRunKey] = React.useState<string | null>(null);

  const invalidateRunQueries = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ai-generation-run'] }),
      queryClient.invalidateQueries({ queryKey: ['ai-generation-run-groups'] }),
      queryClient.invalidateQueries({ queryKey: ['ai-validation-run'] }),
      queryClient.invalidateQueries({ queryKey: ['ai-validation-question-result'] }),
    ]);
  }, [queryClient]);

  const validationCycleMutation = useMutation<
    any,
    Error,
    {
      row: any;
      additionalContext?: string;
      mode: ValidationDialogMode;
    }
  >({
    mutationFn: async ({ row, additionalContext, mode }) =>
      apiPostJson<any>('ai/validation/generation-run/regenerate', {
        generation_run_id: rootGenerationRunIdOf(row),
        current_generation_run_id: currentGenerationRunIdOf(row),
        validation_profile_key: 'default-rag-grounded-validation',
        additional_context: additionalContext || '',
        use_existing_validation: mode === 'revalidate',
        validate_first_if_missing: true,
        regenerate_on_failure: true,
        validate_regenerated_output: true,
      }),
    onMutate: ({ row }) => {
      setValidateError(null);
      setActiveValidationRunKey(stableRunKeyOf(row));
    },
    onSuccess: (data, variables) => {
      const fallbackRun = getCurrentGenerationRun(variables.row);
      const nextGenerationRun = extractGenerationRunFromResponse(data, fallbackRun);
      const nextValidationRun = extractValidationRunFromResponse(data);

      setSelectedGenerationRun((previous) => {
        if (nextGenerationRun) {
          if (previous?.id && String(previous.id) === String(nextGenerationRun.id)) {
            return {
              ...previous,
              ...nextGenerationRun,
            };
          }

          return nextGenerationRun;
        }

        if (nextValidationRun) {
          const base = previous || fallbackRun;

          return {
            ...base,
            latest_validation_run: nextValidationRun,
            latest_validation_run_id: nextValidationRun.id,
            validation_status: nextValidationRun.status,
          };
        }

        return previous || fallbackRun;
      });

      setGenerationDetailFocus('validation');
      setGenerationDetailOpen(true);

      // Keep modal state stable first. Refresh list/table data in the background.
      void invalidateRunQueries();
    },
    onError: (err: any, variables) => {
      setValidateError(err?.message || 'Validation/regeneration cycle failed.');
      setSelectedGenerationRun((previous) => previous || getCurrentGenerationRun(variables.row));
      setGenerationDetailFocus('validation');
      setGenerationDetailOpen(true);
    },
    onSettled: () => {
      setActiveValidationRunKey(null);
    },
  });

  const validating = validationCycleMutation.isPending;

  const openGenerationDetails = React.useCallback(
    (row: any, focus: GenerationDetailFocus = 'overview') => {
      setSelectedGenerationRun(getCurrentGenerationRun(row));
      setGenerationDetailFocus(focus);
      setGenerationDetailOpen(true);
    },
    [],
  );

  const requestValidation = React.useCallback((row: any) => {
    setSelectedGenerationRun(getCurrentGenerationRun(row));
    setValidationDialogMode('validate');
    setPendingValidationRun(row);
  }, []);

  const requestRevalidation = React.useCallback((row: any) => {
    setSelectedGenerationRun(getCurrentGenerationRun(row));
    setValidationDialogMode('revalidate');
    setPendingValidationRun(row);
  }, []);

  const confirmValidation = React.useCallback(
    (additionalContext: string) => {
      if (!pendingValidationRun) return;

      const row = pendingValidationRun;
      const mode = validationDialogMode;

      setPendingValidationRun(null);
      setSelectedGenerationRun(getCurrentGenerationRun(row));
      setGenerationDetailFocus('validation');
      setGenerationDetailOpen(true);

      validationCycleMutation.mutate({
        row,
        additionalContext,
        mode,
      });
    },
    [pendingValidationRun, validationDialogMode, validationCycleMutation],
  );

  return (
    <Box>
      {validateError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidateError(null)}>
          {validateError}
        </Alert>
      ) : null}

      <TuningAdminTable
        title="Generation Runs"
        description="Canonical generated entity/resource scopes. Each row represents the current generated output for a root generation cycle."
        endpoint="ai/validation/generation-run-groups/"
        queryKey={['ai-generation-run-groups']}
        fields={[]}
        allowCreate={false}
        allowEdit={false}
        allowDelete={false}
        selectedId={selectedGenerationRun?.id}
        onRowClick={(row) => openGenerationDetails(row, 'overview')}
        orderBy="-updated_at"
        defaultPageSize={25}
        columns={[
          {
            key: 'entity_name',
            label: 'Entity',
            sortKey: 'entity_name',
            render: (row) => {
              const current = getCurrentGenerationRun(row);

              return (
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={700}>
                    {row.entity_name ||
                      current.entity_name ||
                      `${row.entity_type || current.entity_type}:${row.entity_id || current.entity_id}`}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {row.entity_type || current.entity_type}:{row.entity_id || current.entity_id} /{' '}
                    {row.resource_type || current.resource_type || current.section_key || '-'}
                  </Typography>

                  {row.context_name || current.context_name ? (
                    <Typography variant="caption" color="text.secondary">
                      Context: {safePreview(row.context_name || current.context_name, 120)}
                    </Typography>
                  ) : null}

                  {row.root_generation_run_id ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      Root: {row.root_generation_run_id}
                    </Typography>
                  ) : null}
                </Stack>
              );
            },
          },
          {
            key: 'version_scope',
            label: 'Version Scope',
            width: 230,
            sortable: false,
            render: (row) => <VersionScopeStack run={getCurrentGenerationRun(row)} />,
          },
          {
            key: 'diagnostics',
            label: 'Diagnostics',
            width: 300,
            sortable: false,
            render: (row) => {
              const current = getCurrentGenerationRun(row);
              const chunks = getRagChunks(current);
              const promptWords = countWords(current.prompt_text);
              const responseWords = countWords(current.response_text);

              return (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={current.rag_query ? 'success' : 'default'}
                    label={current.rag_query ? 'RAG query' : 'No RAG query'}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${chunks.length} chunks`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${promptWords} prompt words`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${responseWords} response words`}
                  />
                  {row.generation_attempt_count !== undefined ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={`${row.generation_attempt_count} attempt(s)`}
                    />
                  ) : null}
                  {row.validation_run_count !== undefined ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="secondary"
                      label={`${row.validation_run_count} validation(s)`}
                    />
                  ) : null}
                </Stack>
              );
            },
          },
          {
            key: 'status',
            label: 'Status',
            width: 150,
            sortKey: 'status',
            render: (row) => statusChip(row.status || getCurrentGenerationRun(row).status),
          },
          {
            key: 'model_name',
            label: 'Model',
            width: 170,
            sortKey: 'model_name',
            render: (row) => row.model_name || getCurrentGenerationRun(row).model_name || '-',
          },
          {
            key: 'updated_at',
            label: 'Updated',
            width: 190,
            sortKey: 'updated_at',
            render: (row) => {
              const current = getCurrentGenerationRun(row);
              const value = row.updated_at || current.updated_at || current.created_at;
              return value ? new Date(value).toLocaleString() : '-';
            },
          },
        ]}
        renderRowActions={(row) => {
          const current = getCurrentGenerationRun(row);
          const hasValidation = generationRunHasValidation(row);
          const rowKey = stableRunKeyOf(row);
          const rowIsValidating = validating && activeValidationRunKey === rowKey;

          return (
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                startIcon={<VisibilityIcon fontSize="small" />}
                onClick={(event) => {
                  event.stopPropagation();
                  openGenerationDetails(row, 'overview');
                }}
              >
                Diagnose
              </Button>

              {hasValidation ? (
                <Button
                  size="small"
                  color="secondary"
                  startIcon={<AutorenewIcon fontSize="small" />}
                  disabled={!current.response_text || validating}
                  onClick={(event) => {
                    event.stopPropagation();
                    requestRevalidation(row);
                  }}
                >
                  {rowIsValidating ? 'Running...' : 'Regenerate & Revalidate'}
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={<FactCheckIcon fontSize="small" />}
                  disabled={!current.response_text || validating}
                  onClick={(event) => {
                    event.stopPropagation();
                    requestValidation(row);
                  }}
                >
                  {rowIsValidating ? 'Running...' : 'Validate & Improve'}
                </Button>
              )}
            </Stack>
          );
        }}
      />

      <ValidateConfirmDialog
        open={!!pendingValidationRun}
        run={pendingValidationRun}
        mode={validationDialogMode}
        validating={validating}
        onCancel={() => setPendingValidationRun(null)}
        onConfirm={confirmValidation}
      />

      <GenerationRunDetailDialog
        open={generationDetailOpen}
        run={selectedGenerationRun}
        validating={validating}
        focus={generationDetailFocus}
        onFocusChange={setGenerationDetailFocus}
        onClose={() => setGenerationDetailOpen(false)}
        onValidate={requestValidation}
        onRevalidate={requestRevalidation}
      />
    </Box>
  );
};

export default GenerationRunsPanel;